// Shared filter configuration for various pages.
// This file defines which fields are filterable for each resource
// and how they should behave in the generic SharedFilterBar.

export const agentFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'knowledge_base_id',
    labelKey: 'knowledge_base',
    type: 'string',
  },
  {
    key: 'channel_id',
    labelKey: 'channel',
    type: 'string',
  },
  {
    key: 'name',
    labelKey: 'name',
    type: 'string',
  },
  {
    key: 'is_started',
    labelKey: 'filters_field_status',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_started', value: true },
      { labelKey: 'filters_option_stopped', value: false },
    ],
  },
  {
    key: 'allow_ticket_creation',
    labelKey: 'filters_field_allow_ticket_creation',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_audio',
    labelKey: 'filters_field_process_audio',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_document',
    labelKey: 'filters_field_process_document',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_image',
    labelKey: 'filters_field_process_image',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_video',
    labelKey: 'filters_field_process_video',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'is_consumption_limit_enabled',
    labelKey: 'advanced.enable_limit',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'max_coins_consumption',
    labelKey: 'advanced.max_coins',
    type: 'numberRange',
    minKey: 'max_coins_consumption_min',
    maxKey: 'max_coins_consumption_max',
    min: 1,
  },
  {
    key: 'consumption_duration',
    labelKey: 'advanced.duration',
    type: 'numberRange',
    minKey: 'consumption_duration_min',
    maxKey: 'consumption_duration_max',
    min: 1,
  },
  {
    key: 'snooze_duration',
    labelKey: 'advanced.snooze',
    type: 'numberRange',
    minKey: 'snooze_duration_min',
    maxKey: 'snooze_duration_max',
    min: 1,
  },
  {
    key: 'processing_delay',
    labelKey: 'advanced.delay',
    type: 'numberRange',
    minKey: 'processing_delay_min',
    maxKey: 'processing_delay_max',
    min: 0,
    max: 30,
  },
  {
    key: 'processing_jitter',
    labelKey: 'advanced.jitter_percentage',
    type: 'numberRange',
    minKey: 'processing_jitter_min',
    maxKey: 'processing_jitter_max',
    min: 0,
    max: 100,
  },
  {
    key: 'is_processing_delay_enabled',
    labelKey: 'advanced.enable_delay',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_date_created',
    type: 'dateRange',
    // Backend expects created_at_start / created_at_end
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_last_updated',
    // Date range in the UI (start & end), mapped directly to
    // updated_at_start / updated_at_end for the backend.
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for knowledge bases list (/knowledge-bases)
export const knowledgeBaseFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'agent_id',
    labelKey: 'agent',
    type: 'string',
  },
  {
    key: 'is_disabled',
    labelKey: 'filters_field_disabled',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: false },
      { labelKey: 'filters_option_disabled', value: true },
    ],
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_last_updated',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for transactions list (/transactions, /transactions/me)
export const transactionFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'user_id',
    labelKey: 'user',
    type: 'enum',
    options: [], // populated dynamically
  },
  {
    key: 'disabled_by_id',
    labelKey: 'filters_field_disabled_by',
    type: 'string',
    adminOnly: true,
  },
  {
    key: 'transaction_type',
    labelKey: 'filters_field_transaction_type',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_transaction_type_payment', value: 'payment' },
      { labelKey: 'filters_option_transaction_type_message', value: 'message' },
      { labelKey: 'filters_option_transaction_type_admin', value: 'admin' },
      { labelKey: 'filters_option_transaction_type_system', value: 'system' },
    ],
  },
  {
    key: 'transaction_status',
    labelKey: 'filters_field_status',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_pending', value: 'pending' },
      { labelKey: 'filters_option_completed', value: 'completed' },
      { labelKey: 'filters_option_cancelled', value: 'cancelled' },
    ],
  },
  {
    key: 'is_disabled',
    labelKey: 'filters_field_disabled',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: false },
      { labelKey: 'filters_option_disabled', value: true },
    ],
  },
  {
    key: 'coins_amount_min',
    labelKey: 'filters_field_min_coins',
    type: 'number',
    min: 0,
  },
  {
    key: 'coins_amount_max',
    labelKey: 'filters_field_max_coins',
    type: 'number',
    min: 0,
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
  {
    key: 'disabled_at',
    labelKey: 'filters_field_disabled_at',
    type: 'dateRange',
    startKey: 'disabled_at_start',
    endKey: 'disabled_at_end',
    adminOnly: true,
  },
];

// Filters for payments list (/payments) - used by Billing -> Payment History
// Backend accepts these keys (see validate_get_all_payments):
//  - payment_status, payment_type, currency
//  - total_min/max, coins_amount_min/max
//  - created_at_start/end, paid_at_start/end (and other date ranges)
//  - is_refunded, is_disputed, has_error (booleans)
export const paymentFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'payment_status',
    labelKey: 'filters_field_payment_status',
    type: 'enumMulti',
    sortKey: 'payment_status',
    options: [
      { labelKey: 'filters_option_payment_status_created', value: 'created' },
      { labelKey: 'filters_option_payment_status_processing', value: 'processing' },
      { labelKey: 'filters_option_payment_status_paid', value: 'paid' },
      { labelKey: 'filters_option_payment_status_failed', value: 'failed' },
      { labelKey: 'filters_option_payment_status_refunded', value: 'refunded' },
      { labelKey: 'filters_option_payment_status_partially_refunded', value: 'partially_refunded' },
      { labelKey: 'filters_option_payment_status_disputed', value: 'disputed' },
    ],
  },
  {
    key: 'payment_type',
    labelKey: 'filters_field_payment_type',
    type: 'enumMulti',
    sortKey: 'payment_type',
    options: [
      { labelKey: 'filters_option_payment_type_checkout', value: 'checkout' },
      { labelKey: 'filters_option_payment_type_auto_reload', value: 'auto_reload' },
    ],
  },
  {
    key: 'currency',
    labelKey: 'filters_field_currency',
    type: 'enumMulti',
    sortKey: 'currency',
    options: [
      { labelKey: 'filters_option_currency_usd', value: 'USD' },
      { labelKey: 'filters_option_currency_eur', value: 'EUR' },
      { labelKey: 'filters_option_currency_gbp', value: 'GBP' },
    ],
  },
  {
    key: 'promotion_code',
    labelKey: 'promotion_code',
    type: 'string',
    sortKey: 'promotion_code',
  },
  {
    key: 'billing_country',
    labelKey: 'billing_country',
    type: 'string',
    sortKey: 'billing_country',
  },
  {
    key: 'billing_state',
    labelKey: 'billing_state',
    type: 'string',
    sortKey: 'billing_state',
  },
  {
    key: 'billing_city',
    labelKey: 'billing_city',
    type: 'string',
    sortKey: 'billing_city',
  },
  {
    key: 'billing_postal_code',
    labelKey: 'billing_postal_code',
    type: 'string',
    sortKey: 'billing_postal_code',
  },
  {
    key: 'billing_address_line1',
    labelKey: 'billing_address_line1',
    type: 'string',
    sortKey: 'billing_address_line1',
  },
  {
    key: 'billing_address_line2',
    labelKey: 'billing_address_line2',
    type: 'string',
    sortKey: 'billing_address_line2',
  },
  {
    key: 'product_id',
    labelKey: 'product_id',
    type: 'enumMulti',
    sortKey: 'product_id',
    options: [
      { labelKey: 'coin_package_starter_pack', value: 'STARTER_PACK' },
      { labelKey: 'coin_package_standard_pack', value: 'STANDARD_PACK' },
      { labelKey: 'coin_package_professional_pack', value: 'PROFESSIONAL_PACK' },
      { labelKey: 'coin_package_enterprise_pack', value: 'ENTERPRISE_PACK' },
      { labelKey: 'coin_package_special_pack_i', value: 'SPECIAL_PACK_I' },
      { labelKey: 'coin_package_special_pack_ii', value: 'SPECIAL_PACK_II' },
      { labelKey: 'coin_package_special_pack_iii', value: 'SPECIAL_PACK_III' },
      { labelKey: 'coin_package_special_pack_iv', value: 'SPECIAL_PACK_IV' },
      { labelKey: 'coin_package_special_pack_v', value: 'SPECIAL_PACK_V' },
    ],
  },
  {
    key: 'sub_total',
    labelKey: 'sub_total',
    type: 'numberRange',
    minKey: 'sub_total_min',
    maxKey: 'sub_total_max',
    min: 0,
    sortKey: 'sub_total',
  },
  {
    key: 'total_min',
    labelKey: 'filters_field_min_total',
    type: 'number',
    min: 0,
    sortKey: 'total',
  },
  {
    key: 'total_max',
    labelKey: 'filters_field_max_total',
    type: 'number',
    min: 0,
    sortKey: 'total',
  },
  {
    key: 'coins_amount_min',
    labelKey: 'filters_field_min_coins',
    type: 'number',
    min: 0,
    sortKey: 'coins_amount',
  },
  {
    key: 'coins_amount_max',
    labelKey: 'filters_field_max_coins',
    type: 'number',
    min: 0,
    sortKey: 'coins_amount',
  },
  {
    key: 'discount_amount',
    labelKey: 'discount_amount',
    type: 'numberRange',
    minKey: 'discount_amount_min',
    maxKey: 'discount_amount_max',
    min: 0,
    sortKey: 'discount_amount',
  },
  {
    key: 'tax_amount',
    labelKey: 'tax_amount',
    type: 'numberRange',
    minKey: 'tax_amount_min',
    maxKey: 'tax_amount_max',
    min: 0,
    sortKey: 'tax_amount',
  },
  {
    key: 'refund_amount',
    labelKey: 'refund_amount',
    type: 'numberRange',
    minKey: 'refund_amount_min',
    maxKey: 'refund_amount_max',
    min: 0,
    sortKey: 'refund_amount',
  },
  {
    key: 'dispute_amount',
    labelKey: 'dispute_amount',
    type: 'numberRange',
    minKey: 'dispute_amount_min',
    maxKey: 'dispute_amount_max',
    min: 0,
    sortKey: 'dispute_amount',
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    sortKey: 'created_at',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'paid_at',
    labelKey: 'filters_field_paid_at',
    type: 'dateRange',
    sortKey: 'paid_at',
    startKey: 'paid_at_start',
    endKey: 'paid_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    sortKey: 'updated_at',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
  {
    key: 'refunded_at',
    labelKey: 'refunded_at',
    type: 'dateRange',
    sortKey: 'refunded_at',
    startKey: 'refunded_at_start',
    endKey: 'refunded_at_end',
  },
  {
    key: 'disputed_at',
    labelKey: 'disputed_at',
    type: 'dateRange',
    sortKey: 'disputed_at',
    startKey: 'disputed_at_start',
    endKey: 'disputed_at_end',
  },
  {
    key: 'failed_at',
    labelKey: 'failed_at',
    type: 'dateRange',
    sortKey: 'failed_at',
    startKey: 'failed_at_start',
    endKey: 'failed_at_end',
  },
  {
    key: 'is_refunded',
    labelKey: 'filters_field_refunded',
    type: 'enumMulti',
    sortKey: 'is_refunded',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'is_disputed',
    labelKey: 'filters_field_disputed',
    type: 'enumMulti',
    sortKey: 'is_disputed',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'has_error',
    labelKey: 'filters_field_has_error',
    type: 'enumMulti',
    sortKey: 'has_error',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'refund_reason',
    labelKey: 'refund_reason',
    type: 'string',
    sortKey: 'refund_reason',
  },
  {
    key: 'refund_failure_reason',
    labelKey: 'refund_failure_reason',
    type: 'string',
    sortKey: 'refund_failure_reason',
  },
  {
    key: 'dispute_reason',
    labelKey: 'dispute_reason',
    type: 'string',
    sortKey: 'dispute_reason',
  },
  {
    key: 'dispute_status',
    labelKey: 'dispute_status',
    type: 'string',
    sortKey: 'dispute_status',
  },
  {
    key: 'failure_message_code',
    labelKey: 'failure_message_code',
    type: 'string',
    sortKey: 'failure_message_code',
  },
  {
    key: 'failure_message_data',
    labelKey: 'failure_message_data',
    type: 'string',
    sortKey: 'failure_message_data',
  },
];

// Filters for users list (/users)
export const userFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'first_name',
    labelKey: 'first_name',
    type: 'string',
  },
  {
    key: 'last_name',
    labelKey: 'last_name',
    type: 'string',
  },
  {
    key: 'email',
    labelKey: 'email',
    type: 'string',
  },
  {
    key: 'is_admin',
    labelKey: 'filters_field_role',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_role_admin', value: true },
      { labelKey: 'filters_option_role_user', value: false },
    ],
  },
  {
    key: 'is_verified',
    labelKey: 'filters_field_verified',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_verified', value: true },
      { labelKey: 'filters_option_unverified', value: false },
    ],
  },
  {
    key: 'is_disabled',
    labelKey: 'filters_field_disabled',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: false },
      { labelKey: 'filters_option_disabled', value: true },
    ],
  },
  {
    key: 'gender',
    labelKey: 'filters_field_gender',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_gender_male', value: 'male' },
      { labelKey: 'filters_option_gender_female', value: 'female' },
    ],
  },
  {
    key: 'language',
    labelKey: 'language',
    type: 'enumMulti',
    options: [
      { label: 'English', value: 'en' },
      { label: 'Français', value: 'fr' },
      { label: 'Español', value: 'es' },
      { label: 'Deutsch', value: 'de' },
      { label: '中文', value: 'zh' },
      { label: 'العربية', value: 'ar' },
    ],
  },
  {
    key: 'timezone',
    labelKey: 'timezone',
    type: 'string',
  },
  {
    key: 'timezone_name',
    labelKey: 'timezone_name',
    type: 'string',
  },
  {
    key: 'disabled_by_id',
    labelKey: 'filters_field_disabled_by',
    type: 'string',
  },
  {
    key: 'discount_added_by_id',
    labelKey: 'filters_field_discount_added_by',
    type: 'string',
  },
  {
    key: 'has_received_welcome_credit',
    labelKey: 'has_received_welcome_credit',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'has_completed_tutorial',
    labelKey: 'has_completed_tutorial',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'discount_percentage',
    labelKey: 'discount_percentage',
    type: 'numberRange',
    minKey: 'discount_percentage_min',
    maxKey: 'discount_percentage_max',
    min: 0,
    max: 80,
  },
  {
    key: 'loyalty_level',
    labelKey: 'loyalty_level',
    type: 'numberRange',
    minKey: 'loyalty_level_min',
    maxKey: 'loyalty_level_max',
    min: 0,
    max: 10,
  },
  {
    key: 'available_coins',
    labelKey: 'available_coins',
    type: 'numberRange',
    minKey: 'available_coins_min',
    maxKey: 'available_coins_max',
    min: 0,
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
  {
    key: 'disabled_at',
    labelKey: 'filters_field_disabled_at',
    type: 'dateRange',
    startKey: 'disabled_at_start',
    endKey: 'disabled_at_end',
  },
  {
    key: 'discount_expiry',
    labelKey: 'filters_field_discount_expiry',
    type: 'dateRange',
    startKey: 'discount_expiry_start',
    endKey: 'discount_expiry_end',
  },
  {
    key: 'welcome_credit_at',
    labelKey: 'welcome_credit_at',
    type: 'dateRange',
    startKey: 'welcome_credit_at_start',
    endKey: 'welcome_credit_at_end',
  },
  {
    key: 'discount_added_at',
    labelKey: 'discount_added_at',
    type: 'dateRange',
    startKey: 'discount_added_at_start',
    endKey: 'discount_added_at_end',
  },
];

// Filters for conversations (advanced filters in Messages)
export const conversationFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'contact_id',
    labelKey: 'contact',
    type: 'string',
  },
  {
    key: 'channel_id',
    labelKey: 'channel',
    type: 'string',
  },
  {
    key: 'agent_id',
    labelKey: 'agent',
    type: 'string',
  },
  {
    key: 'status',
    labelKey: 'filters_field_status',
    type: 'enumMulti',
    options: [
      // Backend accepts only ConversationStatus values: 'active' | 'inactive'
      // We keep user-friendly labels but map them to backend values.
      { labelKey: 'filters_option_open', value: 'active' },
      { labelKey: 'filters_option_closed', value: 'inactive' },
    ],
  },
  {
    key: 'contact_type',
    labelKey: 'filters_field_contact_type',
    // Backend currently expects a single contact_type value
    // (ConversationService compares ContactModel.contact_type == value),
    // so we use a single-select enum instead of enumMulti.
    type: 'enum',
    options: [
      { labelKey: 'filters_option_contact_type_admin', value: 'admin' },
      { labelKey: 'filters_option_contact_type_customer', value: 'customer' },
    ],
  },
  {
    key: 'is_ignored',
    labelKey: 'filters_field_ignored',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'is_snoozed',
    labelKey: 'filters_field_snoozed',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'is_seen_by_user',
    labelKey: 'filters_field_seen_by_user',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_seen', value: true },
      { labelKey: 'filters_option_unseen', value: false },
    ],
  },
  {
    key: 'contact_name',
    labelKey: 'contact_name',
    type: 'string',
  },
  {
    key: 'updated_time',
    labelKey: 'filters_field_last_activity',
    type: 'dateRange',
    startKey: 'updated_time_start',
    endKey: 'updated_time_end',
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for credentials list (/credentials)
// Backend accepts these filter keys (see API documentation):
//  - id, user_id, channel_id, channel_type, name, first_name, last_name
//  - email, credential_user_id, is_permanent_token
//  - token_expires_at_start/end, created_at_start/end, updated_at_start/end
export const credentialFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'user_id',
    labelKey: 'user',
    type: 'string',
  },
  {
    key: 'channel_id',
    labelKey: 'channel',
    type: 'string',
  },
  {
    key: 'channel_type',
    labelKey: 'filters_field_channel_type',
    type: 'enumMulti',
    options: [
      { labelKey: 'facebook', value: 'facebook' },
      { labelKey: 'instagram', value: 'instagram' },
      { labelKey: 'whatsapp', value: 'whatsapp' },
      { labelKey: 'telegram', value: 'telegram' },
    ],
  },
  {
    key: 'name',
    labelKey: 'name',
    type: 'string',
  },
  {
    key: 'first_name',
    labelKey: 'first_name',
    type: 'string',
  },
  {
    key: 'last_name',
    labelKey: 'last_name',
    type: 'string',
  },
  {
    key: 'email',
    labelKey: 'email',
    type: 'string',
  },
  {
    key: 'credential_user_id',
    labelKey: 'credential_user_id',
    type: 'string',
  },
  {
    key: 'status',
    labelKey: 'status',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_status_active', value: 'active' },
      { labelKey: 'filters_option_status_token_expired', value: 'token_expired' },
      { labelKey: 'filters_option_status_token_revoked', value: 'token_revoked' },
      { labelKey: 'filters_option_status_insufficient_permissions', value: 'insufficient_permissions' },
      { labelKey: 'filters_option_status_channel_refresh_failed', value: 'channel_refresh_failed' },
      { labelKey: 'filters_option_status_platform_error', value: 'platform_error' },
    ],
  },
  {
    key: 'is_permanent_token',
    labelKey: 'is_permanent_token',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'token_expires_at',
    labelKey: 'token_expires_at',
    type: 'dateRange',
    startKey: 'token_expires_at_start',
    endKey: 'token_expires_at_end',
  },
  {
    key: 'created_at',
    labelKey: 'created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for channels list (/channels)
// Backend accepts these filter keys (see API documentation):
//  - id, agent_id, credential_id, channel_type, external_channel_id
//  - is_synchronization_enabled, is_permanent_token
//  - token_expires_at_start/end, created_at_start/end, updated_at_start/end
// Note: is_virtual filter is automatically applied as false in useChannelFetching hook
export const channelFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'channel_type',
    labelKey: 'filters_field_channel_type',
    type: 'enumMulti',
    options: [
      { labelKey: 'whatsapp', value: 'whatsapp' },
      { labelKey: 'messenger', value: 'messenger' },
      { labelKey: 'facebook', value: 'facebook_page' },
      { labelKey: 'instagram', value: 'instagram' },
    ],
  },
  {
    key: 'agent_id',
    labelKey: 'agent',
    type: 'string',
  },
  {
    key: 'credential_id',
    labelKey: 'credential',
    type: 'string',
  },
  {
    key: 'external_channel_id',
    labelKey: 'external_channel_id',
    type: 'string',
  },
  {
    key: 'is_synchronization_enabled',
    labelKey: 'filters_field_synchronization',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'is_permanent_token',
    labelKey: 'is_permanent_token',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'token_expires_at',
    labelKey: 'token_expires_at',
    type: 'dateRange',
    startKey: 'token_expires_at_start',
    endKey: 'token_expires_at_end',
  },
  {
    key: 'created_at',
    labelKey: 'created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Sort fields for channels
export const channelSortFields = [
  { value: 'channel_type', labelKey: 'filters_field_channel_type' },
  { value: 'external_channel_id', labelKey: 'external_channel_id' },
  { value: 'name', labelKey: 'name' },
  { value: 'is_synchronization_enabled', labelKey: 'filters_field_synchronization' },
  { value: 'token_expires_at', labelKey: 'token_expires_at' },
  { value: 'is_permanent_token', labelKey: 'is_permanent_token' },
  { value: 'created_at', labelKey: 'created_at' },
  { value: 'updated_at', labelKey: 'updated_at' },
];


// Filters for contacts list (/contacts)
// Backend accepts these filter keys (see API documentation):
//  - id, channel_id, agent_id, first_name, last_name, full_name, profile_url, note
//  - contact_type, external_contact_id, locale, timezone, gender, email, phone
//  - is_ignored, is_snoozed, created_at_start/end, updated_at_start/end
export const contactFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'channel_id',
    labelKey: 'channel',
    type: 'string',
  },
  {
    key: 'agent_id',
    labelKey: 'agent',
    type: 'string',
  },
  {
    key: 'first_name',
    labelKey: 'first_name',
    type: 'string',
  },
  {
    key: 'last_name',
    labelKey: 'last_name',
    type: 'string',
  },
  {
    key: 'full_name',
    labelKey: 'full_name',
    type: 'string',
  },
  {
    key: 'profile_url',
    labelKey: 'profile_url',
    type: 'string',
  },
  {
    key: 'note',
    labelKey: 'note',
    type: 'string',
  },
  {
    key: 'contact_type',
    labelKey: 'contact_type',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_contact_type_admin', value: 'admin' },
      { labelKey: 'filters_option_contact_type_customer', value: 'customer' },
    ],
  },
  {
    key: 'gender',
    labelKey: 'gender',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_gender_male', value: 'male' },
      { labelKey: 'filters_option_gender_female', value: 'female' },
    ],
  },
  {
    key: 'is_ignored',
    labelKey: 'filters_field_ignored',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'is_snoozed',
    labelKey: 'filters_field_snoozed',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'external_contact_id',
    labelKey: 'external_contact_id',
    type: 'string',
  },
  {
    key: 'locale',
    labelKey: 'locale',
    type: 'string',
  },
  {
    key: 'timezone',
    labelKey: 'timezone',
    type: 'string',
  },
  {
    key: 'email',
    labelKey: 'email',
    type: 'string',
  },
  {
    key: 'phone',
    labelKey: 'phone',
    type: 'string',
  },
  {
    key: 'created_at',
    labelKey: 'created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Sort fields for contacts
export const contactSortFields = [
  { value: 'first_name', labelKey: 'first_name' },
  { value: 'last_name', labelKey: 'last_name' },
  { value: 'full_name', labelKey: 'full_name' },
  { value: 'contact_type', labelKey: 'filters_field_contact_type' },
  { value: 'locale', labelKey: 'locale' },
  { value: 'email', labelKey: 'email' },
  { value: 'phone', labelKey: 'phone' },
  { value: 'is_ignored', labelKey: 'filters_field_ignored' },
  { value: 'is_snoozed', labelKey: 'filters_field_snoozed' },
  { value: 'created_at', labelKey: 'created_at' },
  { value: 'updated_at', labelKey: 'updated_at' },
];


// Filters for data exports list (/data-exports) - used by Settings -> Data Exports
// Backend accepts:
//  - status (string or list)
//  - created_at_start/end, updated_at_start/end, started_at_start/end, completed_at_start/end
//  - id (encoded id string or list)
export const dataExportFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'status',
    labelKey: 'status',
    type: 'enumMulti',
    options: [
      { labelKey: 'pending', value: 'pending' },
      { labelKey: 'processing', value: 'in_progress' },
      { labelKey: 'completed', value: 'completed' },
      { labelKey: 'failed', value: 'failed' },
    ],
  },
  {
    key: 'format',
    labelKey: 'format',
    type: 'enumMulti',
    options: [
      { label: 'CSV', value: 'csv' },
      { label: 'JSON', value: 'json' },
    ],
  },
  {
    key: 'all_users',
    labelKey: 'all_users',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'user_ids',
    labelKey: 'user_ids',
    type: 'string',
  },
  {
    key: 'progress',
    labelKey: 'progress',
    type: 'numberRange',
    minKey: 'progress_min',
    maxKey: 'progress_max',
    min: 0,
    max: 100,
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
  {
    key: 'started_at',
    labelKey: 'started_at',
    type: 'dateRange',
    startKey: 'started_at_start',
    endKey: 'started_at_end',
  },
  {
    key: 'completed_at',
    labelKey: 'completion_date',
    type: 'dateRange',
    startKey: 'completed_at_start',
    endKey: 'completed_at_end',
  },
  {
    key: 'failed_at',
    labelKey: 'failed_at',
    type: 'dateRange',
    startKey: 'failed_at_start',
    endKey: 'failed_at_end',
  },
];

export const ticketFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'contact_id',
    labelKey: 'contact',
    type: 'string',
  },
  {
    key: 'channel_id',
    labelKey: 'channel',
    type: 'enumMulti',
    options: [], // populated dynamically
  },
  {
    key: 'status',
    labelKey: 'ticket_status',
    type: 'enumMulti',
    options: [
      { labelKey: 'ticket_status_open', value: 'open' },
      { labelKey: 'ticket_status_in_progress', value: 'in_progress' },
      { labelKey: 'ticket_status_resolved', value: 'resolved' },
      { labelKey: 'ticket_status_closed', value: 'closed' },
    ],
  },
  {
    key: 'priority',
    labelKey: 'ticket_priority',
    type: 'enumMulti',
    options: [
      { labelKey: 'ticket_priority_low', value: 'low' },
      { labelKey: 'ticket_priority_medium', value: 'medium' },
      { labelKey: 'ticket_priority_high', value: 'high' },
      { labelKey: 'ticket_priority_urgent', value: 'urgent' },
    ],
  },
  {
    key: 'category',
    labelKey: 'category',
    type: 'enumMulti',
    options: [
      { labelKey: 'ticket_category_escalation', value: 'escalation' },
      { labelKey: 'ticket_category_reclamation', value: 'reclamation' },
      { labelKey: 'ticket_category_suggestion', value: 'suggestion' },
      { labelKey: 'ticket_category_order', value: 'order' },
      { labelKey: 'ticket_category_other', value: 'other' },
    ],
  },
  {
    key: 'opened_by_type',
    labelKey: 'opened_by_type',
    type: 'enumMulti',
    options: [
      { labelKey: 'opened_by_user', value: 'user' },
      { labelKey: 'opened_by_agent', value: 'agent' },
    ],
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'resolved_at',
    labelKey: 'resolved_at',
    type: 'dateRange',
    startKey: 'resolved_at_start',
    endKey: 'resolved_at_end',
  },
  {
    key: 'closed_at',
    labelKey: 'closed_at',
    type: 'dateRange',
    startKey: 'closed_at_start',
    endKey: 'closed_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for knowledge entries (/knowledge-bases/:id/entries)
export const knowledgeEntryFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'knowledge_base_id',
    labelKey: 'knowledge_base',
    type: 'string',
  },
  {
    key: 'entry_type',
    labelKey: 'entry_type',
    type: 'enumMulti',
    options: [
      { labelKey: 'qa_entry', value: 'question_answer' },
      { labelKey: 'business_info', value: 'business_info' },
    ],
  },
  {
    key: 'is_disabled',
    labelKey: 'filters_field_disabled',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: false },
      { labelKey: 'filters_option_disabled', value: true },
    ],
  },
  {
    key: 'created_at',
    labelKey: 'created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

export const knowledgeEntrySortFields = [
  { value: 'id', labelKey: 'id' },
  { value: 'entry_type', labelKey: 'entry_type' },
  { value: 'is_disabled', labelKey: 'filters_field_disabled' },
  { value: 'created_at', labelKey: 'created_at' },
  { value: 'updated_at', labelKey: 'updated_at' },
];
